import { observable, runInAction } from "mobx";
import { RecordingUpdated } from "../StateEvents";
import { DbRecording, ISODateTimeString, UUID } from "./DatabaseTypes";
import { DbRecordingEntryReader } from "./DbRecordingEntryReader";
import { requireEvidenceDatabase } from "./getDatabase";
import type { EvidenceDatabase } from "./EvidenceDbStruct";
import { TabsBeingRecorded } from "./TabsBeingRecordedDatabaseTable";
import { DevToolsPanelAPIClient } from "../../lib/API/DevToolsApiClient";


export class DbRecordingEntryWriter extends DbRecordingEntryReader {

	constructor(db: EvidenceDatabase, recordingUuid: UUID, recording: DbRecording | undefined) {
		super(db, recordingUuid, recording);
		runInAction(() => {
			DbRecordingEntryWriter.#forRecordingUuid.set(recordingUuid, this);
		});
	}

	static updateRecording = async (recordingUuid: UUID, fieldsToChange: Partial<Omit<DbRecording, "recordingUuid">>) => {
		// Update within a transaction
		const db = await requireEvidenceDatabase();
		const tx = db.transaction("recordings", "readwrite");
		try {
			// Read the old value
			const oldRecording = await tx.store.get(recordingUuid);
			if (oldRecording == null) {
				throw new Error(`Can't update a non-existent recording record for recordingUuid ${recordingUuid}`);
			}
			// Update the modified fields
			const recording = { ...oldRecording, ...fieldsToChange };
			const keysOfNullValueFields =  Object.entries(fieldsToChange).filter(([_,value]) => value == null).map(([key]) => key);
			for (const key of keysOfNullValueFields) {
				delete (recording as Record<string, unknown>)[key];
			}
			await tx.store.put(recording);
			// ProcessInfo.log(`Updated recording ${recordingUuid}`, recording, fieldsToChange, oldRecording);
			// End the transaction so that others can write
			tx.commit();
			// Announce the change so that all other copies of the recording object are updated from the database.
			RecordingUpdated.send({ recordingUuid });
			DbRecordingEntryReader.fromRecordingUuid(recordingUuid)?.setRecordingForThisObjectOnly(recording);
			DbRecordingEntryWriter.fromRecordingUuid(recordingUuid)?.setRecordingForThisObjectOnly(recording);
			return recording;
		} catch (e) {
			tx.abort();
			throw e;
		}
	};


	/**
	 * Updates the recording recording in the database (for the recordingUuid scoped to this class instance)
	 * and announces the update to other processes.
	 *
	 * @param updatedFields Any fields of the recording record to change.
	 */
	updateRecording = async (fieldsToChange: Partial<Omit<DbRecording, "recordingUuid">>) => {
		const {recordingUuid} = this;
		const recording = await DbRecordingEntryWriter.updateRecording(recordingUuid, fieldsToChange);
		this.setRecordingForThisObjectOnly(recording);
		return recording;
	};

	/**
	 * Stop recording and update its summary information.
	 * 
	 * NOTE: does not stop video recording or update icons. Caller must do that!
	 */
	stopRecording = async () => {
		const {recordingUuid} = this;
		const tabIds = await TabsBeingRecorded.getAllForRecording({recordingUuid});
		if (tabIds.length > 0) {
			DevToolsPanelAPIClient.getAndSaveHAR({tabIds, recordingUuid});
		}
		await Promise.all([
			this.updateRecording({
				status: "stopped",
				stopTimeIso: ISODateTimeString(),
				videoRecordingInProgress: undefined
			}),
			...(tabIds.length <= 0 ? [] : [
				TabsBeingRecorded.removeAllForRecording({recordingUuid: this.recordingUuid}),
			])
		]);
	}

	setName = async (name: string | undefined) => {
		await this.updateRecording({name});
	}


	static #forRecordingUuid = observable.map<UUID, DbRecordingEntryWriter>();

	static override fromRecordingUuid = (recordingUuid: UUID) => {
		return DbRecordingEntryWriter.#forRecordingUuid.get(recordingUuid);
	}

	static override fromDbAndRecording = (db: EvidenceDatabase, recording: DbRecording) => {
		let writer = DbRecordingEntryWriter.#forRecordingUuid.get(recording.recordingUuid);
		if (writer == null) {
			writer = new DbRecordingEntryWriter(db, recording.recordingUuid, recording);
		} else if (writer.recording == null) {
			writer.setRecordingForThisObjectOnly(recording);
		}
		return writer;
	}

	static override fromRecordingUuidDeferredLoad = (db: EvidenceDatabase, recordingUuid: UUID) => {
		return DbRecordingEntryWriter.#forRecordingUuid.get(recordingUuid) ?? new DbRecordingEntryWriter(db, recordingUuid, undefined);
	}

	static override fromRecordingUuidAwaitLoad = async (db: EvidenceDatabase, recordingUuid: UUID) => {
		return DbRecordingEntryWriter.#forRecordingUuid.get(recordingUuid) ?? new DbRecordingEntryWriter(db, recordingUuid, await DbRecordingEntryReader.fetchFromDb(db, recordingUuid));
	}

}
